import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from 'services'
import { useSnackbar } from 'contexts'

// ── Types ────────────────────────────────────────────────────────────────────

export type PermissionOperation = 'read' | 'create' | 'update' | 'delete' | 'action'

export interface PermissionMeta {
  name: string
  label: string
  /** CRUD + Action axis (matrix operation filter). May be absent on older payloads. */
  operation?: PermissionOperation
  /** 'internal' = Vera-staff-only; excluded from the company grid server-side. */
  scope?: 'internal'
  /** One-line explanation shown via an info-tooltip next to the label. Optional —
   * only backfilled permissions carry it (packages/types permission-groups.ts). */
  description?: string
}

export interface PermissionGroup {
  key: string
  label: string
  scope?: 'internal'
  permissions: PermissionMeta[]
}

export interface MatrixRoleItem {
  id: string
  name: string
  isCustom: boolean
  permissionNames: string[]
  /** Parallel array to permissionNames — DB UUIDs for use in PUT .../permissions. */
  permissionIds: string[]
  /** True when this built-in role has a company-level permissions override. */
  isOverridden: boolean
  /**
   * The global/built-in role id — set for built-in roles (both overridden and
   * unmodified). Undefined for standalone custom roles.
   */
  builtinRoleId?: string
}

export interface RolesMatrixResponse {
  roles: MatrixRoleItem[]
  groups: PermissionGroup[]
}

// PUT /roles/company/custom/:id/permissions body
interface UpdateRolePermissionsPayload {
  id: string
  permissionIds: string[]
}

// POST /roles/company/override body
interface CreateRoleOverridePayload {
  roleId: string
  permissionIds: string[]
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const rolesMatrixQueryKeys = {
  matrix: () => ['roles', 'company', 'matrix'] as const,
}

// ── Query hook ────────────────────────────────────────────────────────────────

export const useRolesMatrix = () => {
  return useQuery<RolesMatrixResponse>({
    queryKey: rolesMatrixQueryKeys.matrix(),
    queryFn: async () => {
      const response = await api.get('/roles/company/matrix')
      // Guarantee a well-formed shape so the matrix never maps over a non-array
      // (e.g. a "No company access" error body for users without a company).
      const body = response.data
      return {
        roles: Array.isArray(body?.roles) ? body.roles : [],
        groups: Array.isArray(body?.groups) ? body.groups : [],
      }
    },
    staleTime: 1000 * 60 * 2,
  })
}

// ── Mutation hook ─────────────────────────────────────────────────────────────

/**
 * The shared `api` wrapper RESOLVES (does not reject) on 4xx/5xx — its
 * interceptor returns `error.response`. Without this guard a 403/500 flows into
 * react-query's `onSuccess`, firing a false success toast while nothing changed
 * (the bug behind "Reset to default didn't work"). Throw so the failure is
 * routed to `onError` and surfaced to the user.
 */
export const assertOk = (res: any) => {
  if (!res || typeof res.status !== 'number' || res.status >= 400) {
    const msg = res?.data?.message ?? `Request failed (${res?.status ?? 'network error'})`
    throw new Error(Array.isArray(msg) ? msg.join(', ') : String(msg))
  }
  return res
}

export const useUpdateRolePermissions = () => {
  const queryClient = useQueryClient()
  const { open: notify } = useSnackbar()

  return useMutation<void, Error, UpdateRolePermissionsPayload>({
    mutationFn: ({ id, permissionIds }) =>
      api.put(`/roles/company/custom/${id}/permissions`, { permissionIds }).then(assertOk).then(() => undefined),
    onSuccess: () => {
      // Invalidate both the matrix and the role list so all consumers refresh
      queryClient.invalidateQueries({ queryKey: rolesMatrixQueryKeys.matrix() })
      queryClient.invalidateQueries({ queryKey: ['roles', 'company'] })
      notify({ type: 'success', title: 'Permissions updated' })
    },
    onError: () => {
      notify({ type: 'error', title: 'Failed to update permissions' })
    },
  })
}

/**
 * Creates (or replaces) a company override for a built-in role.
 * POST /roles/company/override  body: { roleId, permissionIds }
 */
export const useCreateRoleOverride = () => {
  const queryClient = useQueryClient()
  const { open: notify } = useSnackbar()

  return useMutation<void, Error, CreateRoleOverridePayload>({
    mutationFn: ({ roleId, permissionIds }) =>
      api.post('/roles/company/override', { roleId, permissionIds }).then(assertOk).then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesMatrixQueryKeys.matrix() })
      queryClient.invalidateQueries({ queryKey: ['roles', 'company'] })
      notify({ type: 'success', title: 'Permissions updated' })
    },
    onError: () => {
      notify({ type: 'error', title: 'Failed to update permissions' })
    },
  })
}

/**
 * Resets (deletes) the company override for a built-in role, reverting it to
 * the global defaults.
 * DELETE /roles/company/override/:roleId
 */
export const useResetRoleOverride = () => {
  const queryClient = useQueryClient()
  const { open: notify } = useSnackbar()

  return useMutation<void, Error, string>({
    mutationFn: (roleId) =>
      api.delete(`/roles/company/override/${roleId}`).then(assertOk).then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesMatrixQueryKeys.matrix() })
      queryClient.invalidateQueries({ queryKey: ['roles', 'company'] })
    },
    onError: () => {
      notify({ type: 'error', title: 'Failed to reset role' })
    },
  })
}
